/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import { useMutation } from '@apollo/client';
import * as Sentry from '@sentry/react';

import { useRetracked } from 'js/lib/retracked';
import { get } from 'js/lib/user';

import { InlineNotification, breakpoints } from '@coursera/cds-core';

import type { VivaExamQuestionsType } from 'bundles/academic-integrity/components/viva-exam/VivaExamSubmission';
import VivaExamSubmitQuestionAnswerMutation from 'bundles/academic-integrity/components/viva-exam/api/VivaExamSubmitQuestionAnswerMutation.graphql';
import type {
  VivaExamSubmitQuestionAnswerMutationMutation as VivaExamSubmitQuestionAnswerMutationType,
  VivaExamSubmitQuestionAnswerMutationMutationVariables as VivaExamSubmitQuestionAnswerMutationVariables,
} from 'bundles/academic-integrity/components/viva-exam/api/__generated__/VivaExamSubmitQuestionAnswerMutation';
import IntegrityCheckChatFooter from 'bundles/academic-integrity/components/viva-exam/integrity-check-chat/IntegrityCheckChatFooter';
import IntegrityCheckChatHeader from 'bundles/academic-integrity/components/viva-exam/integrity-check-chat/IntegrityCheckChatHeader';
import IntegrityCheckChatInput from 'bundles/academic-integrity/components/viva-exam/integrity-check-chat/IntegrityCheckChatInput';
import IntegrityCheckChatMessage from 'bundles/academic-integrity/components/viva-exam/integrity-check-chat/IntegrityCheckChatMessage';
import type { MessageType } from 'bundles/academic-integrity/components/viva-exam/integrity-check-chat/lib/constants';
import {
  MessageTypes,
  finalMessageText,
  retriableMessageText,
} from 'bundles/academic-integrity/components/viva-exam/integrity-check-chat/lib/constants';
import { isVivaExamFeedbackEnabled } from 'bundles/academic-integrity/lib/epics';
import useWindowFocusEventsTracking from 'bundles/assess-common/hooks/useWindowFocusEventsTracking';
import type { AcademicIntegritySettingsFragment } from 'bundles/assignment-submission/components/api/assignment-submission-api/__generated__/AssignmentSubmission';

import _t from 'i18n!nls/academic-integrity';

type VivaExamSettings = AcademicIntegritySettingsFragment['vivaExamSettings'];
const styles = {
  outerContainer: css`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgb(255 255 255);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 64px 316px;
    box-sizing: border-box;
    z-index: 10000;

    ${breakpoints.down('md')} {
      padding: 64px 10vw;
    }

    ${breakpoints.down('sm')} {
      padding: 32px 5vw;
    }
  `,
  chatContainer: css`
    background: var(--cds-color-neutral-background-primary-weak);
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    width: 100%;
    height: 100%;
    border-radius: var(--cds-spacing-200);
  `,

  messagesContainer: css`
    flex-grow: 1;
    overflow-y: auto;
    padding: var(--cds-spacing-50) var(--cds-spacing-400) var(--cds-spacing-400) var(--cds-spacing-400);
  `,
};

type Message = {
  id: string;
  type: MessageType;
  text: string;
};

type IntegrityCheckChatProps = {
  onDismiss: () => void;
  startExamLoading?: boolean;
  totalQuestionsCount: number;
  firstQuestion?: string | null;
  vivaExamSessionId?: string | null;
  hasErrorFromInitialQuestionAPI?: boolean;
  vivaExamQuestions?: VivaExamQuestionsType;
  assignmentId?: string;
  submitterFirstName?: string;
  courseId?: string;
  refetchAssignmentSubmissionData?: () => Promise<void>;
  vivaExamSettings?: VivaExamSettings;
};

type UseSubmitAnswerProps = {
  vivaExamSessionId?: string | null;
  setConversation: React.Dispatch<React.SetStateAction<Message[]>>;
  setQuestionsAnsweredCount: React.Dispatch<React.SetStateAction<number>>;
  setIsExamComplete: React.Dispatch<React.SetStateAction<boolean>>;
  setHasNonRetriableError: React.Dispatch<React.SetStateAction<boolean>>;
  questionsAnsweredCount: number;
  setLearningFeedback: React.Dispatch<React.SetStateAction<string>>;
};

type UseConversationStateProps = {
  vivaExamQuestions?: VivaExamQuestionsType;
  firstQuestion?: string | null;
  isExamComplete: boolean;
  setQuestionsAnsweredCount: React.Dispatch<React.SetStateAction<number>>;
  courseId?: string;
  learnerFeedback?: string;
  vivaExamSettings?: VivaExamSettings;
};

// Custom hook for managing the conversation state
const useConversationState = ({
  vivaExamQuestions,
  firstQuestion,
  isExamComplete,
  setQuestionsAnsweredCount,
  courseId,
  learnerFeedback,
  vivaExamSettings,
}: UseConversationStateProps): [Message[], React.Dispatch<React.SetStateAction<Message[]>>] => {
  const [conversation, setConversation] = useState<Message[]>([]);
  const vivaExamFeedbackEnabled =
    isVivaExamFeedbackEnabled(courseId) || vivaExamSettings?.status === 'ENABLED_WITH_FEEDBACK';

  useEffect(() => {
    if (vivaExamQuestions) {
      setQuestionsAnsweredCount(vivaExamQuestions.examSession.questions.length);
      setConversation(
        vivaExamQuestions.examSession.questions.flatMap((item, index) => [
          { id: `coach-message-${index}`, type: MessageTypes.COACH, text: item.question },
          { id: `learner-response-${index}`, type: MessageTypes.LEARNER, text: item.response || '' },
        ])
      );
    } else if (firstQuestion) {
      setConversation([{ id: `message-${Date.now()}`, type: MessageTypes.COACH, text: firstQuestion }]);
    }
  }, [vivaExamQuestions, firstQuestion, setQuestionsAnsweredCount]);

  useEffect(() => {
    if (vivaExamFeedbackEnabled && learnerFeedback) {
      setConversation((prevConversation) => [
        ...prevConversation,
        { id: `message-${Date.now()}`, type: MessageTypes.COACH, text: learnerFeedback },
      ]);
    }
  }, [learnerFeedback, vivaExamFeedbackEnabled]);

  useEffect(() => {
    if (isExamComplete && !vivaExamFeedbackEnabled) {
      setConversation((prevConversation) => [
        ...prevConversation,
        { id: `message-${Date.now()}`, type: MessageTypes.COACH, text: finalMessageText },
      ]);
    }
  }, [isExamComplete, vivaExamFeedbackEnabled]);

  return [conversation, setConversation];
};
// Custom hook for handling submitting answers
const useSubmitAnswer = ({
  vivaExamSessionId,
  setConversation,
  setQuestionsAnsweredCount,
  setIsExamComplete,
  setHasNonRetriableError,
  questionsAnsweredCount,
  setLearningFeedback,
}: UseSubmitAnswerProps) => {
  const [submitQuestionAnswer, { loading: submitAnswerLoading }] = useMutation<
    VivaExamSubmitQuestionAnswerMutationType,
    VivaExamSubmitQuestionAnswerMutationVariables
  >(VivaExamSubmitQuestionAnswerMutation, {
    context: { clientName: 'gatewayGql' },
  });

  const [retriableError, setRetriableError] = useState(false);
  const [retriableAnswer, setRetriableAnswer] = useState('');

  const handleSubmitAnswer = async (answer: string): Promise<void> => {
    // Generate a unique ID for the new message
    const answerMessageId = `message-${Date.now()}`;

    // Add the learner's answer to the conversation
    setConversation((prevConversation) => [
      ...prevConversation,
      { id: answerMessageId, type: MessageTypes.LEARNER, text: answer },
    ]);
    setQuestionsAnsweredCount((prevCount) => prevCount + 1);

    // Call the mutation with the examSessionId and the learner's answer
    try {
      if (vivaExamSessionId) {
        const { data } = await submitQuestionAnswer({
          variables: {
            examSessionId: vivaExamSessionId,
            response: answer,
          },
        });

        if (data?.VivaExam_SubmitQuestionAnswer?.__typename === 'VivaExam_SubmitQuestionAnswerSuccess') {
          const examSession = data.VivaExam_SubmitQuestionAnswer.examSession;
          const questions = examSession?.questions;

          setRetriableError(false);
          setRetriableAnswer('');
          setIsExamComplete(!!examSession?.isComplete);
          setLearningFeedback(examSession?.learnerFeedback ?? '');

          const nextQuestion = questions?.[questionsAnsweredCount + 1]?.question;
          // Setting the next question from the API and displaying it to the chat
          if (nextQuestion) {
            const nextQuestionMessageId = `message-${Date.now()}`;
            setConversation((prevConversation) => [
              ...prevConversation,
              { id: nextQuestionMessageId, type: MessageTypes.COACH, text: nextQuestion },
            ]);
          }
        } else if (data?.VivaExam_SubmitQuestionAnswer?.__typename === 'VivaExam_SubmitQuestionAnswerFailure') {
          const errors = data.VivaExam_SubmitQuestionAnswer.errors;
          const isRetriableError = errors?.some((error) => error.__typename === 'VivaExam_Retriable_Error');

          if (isRetriableError) {
            // Set the retriable error state and pre-fill the answer
            setRetriableError(true);
            setRetriableAnswer(answer);
            // Add a static retriable message from Coach to the conversation
            setConversation((prevConversation) => [
              ...prevConversation,
              {
                id: `coach-retriable-error-${Date.now()}`,
                type: MessageTypes.COACH,
                text: retriableMessageText,
              },
            ]);
            setQuestionsAnsweredCount((prevCount) => prevCount - 1);
          }
        }
      }
    } catch (error) {
      setHasNonRetriableError(true);
      Sentry.captureException(error);
    }
  };

  return { handleSubmitAnswer, submitAnswerLoading, retriableError, retriableAnswer };
};

const IntegrityCheckChat = ({
  onDismiss,
  totalQuestionsCount,
  startExamLoading,
  firstQuestion,
  vivaExamSessionId,
  hasErrorFromInitialQuestionAPI,
  vivaExamQuestions,
  assignmentId,
  submitterFirstName,
  courseId,
  refetchAssignmentSubmissionData,
  vivaExamSettings,
}: IntegrityCheckChatProps) => {
  const [isExamComplete, setIsExamComplete] = useState(false);
  const [hasNonRetriableError, setHasNonRetriableError] = useState(false);
  const [questionsAnsweredCount, setQuestionsAnsweredCount] = useState(0);
  const [learnerFeedback, setLearningFeedback] = useState('');

  const track = useRetracked();

  // Determine if there is data in vivaExamQuestions
  const hasVivaExamQuestionsData = Boolean(vivaExamQuestions?.examSession?.questions?.length);

  const [conversation, setConversation] = useConversationState({
    vivaExamQuestions,
    firstQuestion,
    isExamComplete,
    setQuestionsAnsweredCount,
    courseId,
    learnerFeedback,
    vivaExamSettings,
  });

  const { handleSubmitAnswer, submitAnswerLoading, retriableAnswer, retriableError } = useSubmitAnswer({
    vivaExamSessionId,
    setConversation,
    setQuestionsAnsweredCount,
    setIsExamComplete,
    setHasNonRetriableError,
    questionsAnsweredCount,
    setLearningFeedback,
  });

  useWindowFocusEventsTracking({
    focusEventName: 'viva_exam_learner_focus',
    visibilityEventName: 'viva_exam_learner_visibility',
    // eslint-disable-next-line camelcase
    extraTrackingData: { assignment_id: assignmentId, vivaExamSessionId },
  });

  useEffect(() => {
    // Only attach the beforeunload event listener if vivaExamQuestions (which would be the read only view) is not defined
    if (!vivaExamQuestions) {
      const handleBeforeUnload = (event: BeforeUnloadEvent) => {
        event.preventDefault();
        // Need to assign a return value in order for it to work on chrome
        // eslint-disable-next-line no-param-reassign
        event.returnValue = '';
      };

      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
    return () => {};
  }, [vivaExamQuestions]);

  useEffect(() => {
    // We need to refetch the submission status because the grading could have completed, so that when the learner closes
    // the chat dialog, we can determine if the AIGrading is complete and redirect to the review page
    if (isExamComplete) {
      refetchAssignmentSubmissionData?.();
    }
  }, [isExamComplete, refetchAssignmentSubmissionData]);

  // Auto-scroll to the most recent message when the conversation updates
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversation]);

  const recordAction = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    track({
      trackingName: 'viva_exam_copy_text',
      trackingData: {
        userId: get().id,
        // eslint-disable-next-line camelcase
        assignment_id: assignmentId,
        vivaExamSessionId,
      },
      action: e.type,
    });
  };

  return (
    <div css={styles.outerContainer} onCopy={recordAction} onCut={recordAction}>
      <div css={styles.chatContainer}>
        {(hasNonRetriableError || hasErrorFromInitialQuestionAPI) && (
          <InlineNotification severity="error">
            {_t('There was an issue with the Integrity Check. Please contact your course admin.')}
          </InlineNotification>
        )}
        <IntegrityCheckChatHeader />
        <div css={styles.messagesContainer}>
          {startExamLoading && !firstQuestion && <IntegrityCheckChatMessage messageType={MessageTypes.LOADING} />}
          {conversation.map((message) => {
            return (
              <IntegrityCheckChatMessage
                key={message.id}
                message={message.text}
                messageType={message.type}
                submitterFirstName={submitterFirstName}
              />
            );
          })}
          {submitAnswerLoading && <IntegrityCheckChatMessage messageType={MessageTypes.LOADING} />}
          <div ref={messagesEndRef} />
        </div>
        {!isExamComplete && !hasVivaExamQuestionsData && (
          <IntegrityCheckChatInput
            onSubmit={handleSubmitAnswer}
            isLoading={startExamLoading || submitAnswerLoading}
            retriableAnswer={retriableError ? retriableAnswer : undefined}
            assignmentId={assignmentId}
            vivaExamSessionId={vivaExamSessionId}
          />
        )}
        <IntegrityCheckChatFooter
          totalQuestions={totalQuestionsCount}
          isExamComplete={isExamComplete}
          onDismiss={onDismiss}
          isReadOnly={hasVivaExamQuestionsData}
          questionsAnsweredCount={questionsAnsweredCount}
        />
      </div>
    </div>
  );
};

export default IntegrityCheckChat;
